<template>
    <div class="image-magnifier">
        <div class="bigpic" ref="imgBox" @mousemove="handleMouseMove" @mouseleave="hideMagnifier">

            <el-image fit="fill" class="p" :src="imgUrl" ref="elImageRef" :preview-src-list="[imgUrl]"
                @load="imgLoaded">
            </el-image>

            <div class="magnifier-mask" v-show="showMask" :style="maskStyle"></div>
            <div class="magnifier-preview" v-show="showPreview" :style="previewStyle">
                <img :src="imgUrl" :style="previewImgStyle" alt="放大预览" />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ImageMagnifier',
    props: {
        imgUrl: { type: String, required: true },
        scale: { type: Number, default: 3 },
        maskSize: { type: Number, default: 100 }
    },
    data() {
        return {
            showMask: false,
            showPreview: false,
            maskStyle: {},
            previewStyle: {},
            previewImgStyle: {},
            imgWidth: 0,
            imgHeight: 0,
            boxRect: null,
            lastScrollTop: 0, // 记录最后滚动位置，用于计算偏移
            lastScrollLeft: 0 // 记录水平滚动位置
        };
    },
    methods: {
        imgLoaded(e) {
            const naturalWidth = e.target.naturalWidth;
            const naturalHeight = e.target.naturalHeight;
            const maxWidth = 777;
            const maxHeight = 553;

            const widthRatio = naturalWidth / maxWidth;
            const heightRatio = naturalHeight / maxHeight;
            const ratio = Math.max(widthRatio, heightRatio, 1);

            this.imgWidth = naturalWidth / ratio;
            this.imgHeight = naturalHeight / ratio;

            this.$refs.imgBox.style.width = `${this.imgWidth}px`;
            this.$refs.imgBox.style.height = `${this.imgHeight}px`;
            this.$nextTick(() => {
                this.updateBoxRect();
                // 初始化滚动位置
                this.lastScrollTop = window.scrollY;
                this.lastScrollLeft = window.scrollX;
            });
        },

        // 核心：更新容器位置（兼容滚动）
        updateBoxRect() {
            if (this.$refs.imgBox) {
                // getBoundingClientRect会受滚动影响，每次都重新获取最新位置
                this.boxRect = this.$refs.imgBox.getBoundingClientRect();
            }
        },

        // 窗口缩放/页面滚动时统一处理
        handleWindowChange() {
            this.updateBoxRect();
            // 如果预览框显示，重新计算位置（基于当前鼠标位置）
            if (this.showPreview) {
                // 计算当前遮罩中心位置（相对于视口）
                const maskCenterX = this.boxRect.left + parseFloat(this.maskStyle.left || 0) + this.maskSize / 2;
                const maskCenterY = this.boxRect.top + parseFloat(this.maskStyle.top || 0) + this.maskSize / 2;
                // 模拟鼠标移动事件，更新所有位置
                this.handleMouseMove({
                    clientX: maskCenterX,
                    clientY: maskCenterY
                });
            }
            // 更新滚动记录
            this.lastScrollTop = window.scrollY;
            this.lastScrollLeft = window.scrollX;
        },

        handleMouseMove(e) {
            if (!this.imgWidth || !this.imgHeight) return;
            // 每次鼠标移动都更新容器位置（兼容实时滚动）
            this.updateBoxRect();

            // 计算鼠标相对于图片容器的坐标（不受滚动/窗口缩放影响）
            const x = (e.clientX - this.boxRect.left) / this.imgWidth;
            const y = (e.clientY - this.boxRect.top) / this.imgHeight;
            const clampX = Math.max(0, Math.min(x, 1));
            const clampY = Math.max(0, Math.min(y, 1));

            if (x < 0 || x > 1 || y < 0 || y > 1) {
                this.hideMagnifier();
                return;
            }

            this.showMask = true;
            this.showPreview = true;

            // 遮罩位置计算（精准，不受滚动影响）
            const maskHalf = this.maskSize / 2;
            const maskLeft = clampX * this.imgWidth - maskHalf;
            const maskTop = clampY * this.imgHeight - maskHalf;
            const finalMaskLeft = Math.max(0, Math.min(maskLeft, this.imgWidth - this.maskSize));
            const finalMaskTop = Math.max(0, Math.min(maskTop, this.imgHeight - this.maskSize));

            this.maskStyle = {
                left: `${finalMaskLeft}px`,
                top: `${finalMaskTop}px`,
                width: `${this.maskSize}px`,
                height: `${this.maskSize}px`,
                position: 'absolute',
                background: 'rgba(255,255,255,0.3)',
                border: '1px solid #ccc',
                pointerEvents: 'none',
                zIndex: 9999,
                boxSizing: 'border-box'
            };

            // 预览框位置（智能避让+兼容滚动）
            const previewSize = this.maskSize * this.scale;
            let previewLeft = this.boxRect.right + 20;
            let previewTop = this.boxRect.top;

            // 右侧放不下时，移到原图下方
            if (previewLeft + previewSize > window.innerWidth) {
                previewLeft = this.boxRect.left;
                previewTop = this.boxRect.bottom + 20;
            }

            this.previewStyle = {
                left: `${previewLeft}px`,
                top: `${previewTop}px`,
                width: `${previewSize}px`,
                height: `${previewSize}px`,
                position: 'fixed', // fixed定位，基于视口，滚动时位置稳定
                overflow: 'hidden',
                border: '1px solid #eee',
                zIndex: 9999,
                background: '#fff',
                pointerEvents: 'none',
                boxSizing: 'border-box'
            };

            // 预览图偏移计算（精准）
            const previewHalf = previewSize / 2;
            const maxOffsetX = Math.max(0, (this.imgWidth * this.scale) - previewSize);
            const maxOffsetY = Math.max(0, (this.imgHeight * this.scale) - previewSize);
            const offsetX = Math.max(0, Math.min(clampX * this.imgWidth * this.scale - previewHalf, maxOffsetX));
            const offsetY = Math.max(0, Math.min(clampY * this.imgHeight * this.scale - previewHalf, maxOffsetY));

            this.previewImgStyle = {
                width: `${this.imgWidth * this.scale}px`,
                height: `${this.imgHeight * this.scale}px`,
                marginLeft: `-${offsetX}px`,
                marginTop: `-${offsetY}px`,
                display: 'block'
            };
        },

        hideMagnifier() {
            this.showMask = false;
            this.showPreview = false;
        }
    },
    mounted() {
        // 监听窗口缩放 + 页面滚动
        window.addEventListener('resize', this.handleWindowChange);
        window.addEventListener('scroll', this.handleWindowChange);
    },
    beforeDestroy() {
        // 移除所有监听，避免内存泄漏
        window.removeEventListener('resize', this.handleWindowChange);
        window.removeEventListener('scroll', this.handleWindowChange);
    }
};
</script>

<style lang="scss" scoped>
.image-magnifier {
    position: relative;
    margin-right: 25px;
    display: flex;
    align-items: center;
    justify-content: center;

    .bigpic {
        position: relative;
        display: inline-block;
        border: 1px solid #eee;
        cursor: crosshair;
        user-select: none;
        line-height: 0;
        font-size: 0;

        :deep(.el-image) {
            width: 100% !important;
            height: 100% !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
        }

        :deep(.el-image__wrapper) {
            width: 100% !important;
            height: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
        }

        :deep(.el-image__inner) {
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            cursor: crosshair;

        }

        :deep(.el-image__preview) {
            z-index: 10000 !important;
        }
    }

    .magnifier-mask {
        z-index: 9999 !important;
    }

    .magnifier-preview {
        z-index: 9999 !important;
        pointer-events: none !important;
    }
}
</style>